import React, { useEffect, useRef, useState } from 'react';
import { Mic, MicOff, Send, Volume2, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Card, SectionTitle, inputCls, btnPrimary, btnAccent, btnGhost } from './MlmShell';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EXAMPLES = [
  'Dame el resumen del día',
  'Añade el prospecto Ana García de Instagram, interesada en el negocio',
  'Genera un post para TikTok para atraer nuevos distribuidores',
  'Redacta un email de seguimiento para clientes',
  'Programa seguimiento con Ana en 3 días',
  'Prepara un mensaje de WhatsApp para Ana sobre el negocio',
  'Registra una venta de Optimal-Set a Ana por 170 euros',
  'Ana se registró como nueva socia',
];

export const MlmAynara = ({ workspace, api, busy, setSection }) => {
  const [text, setText] = useState('');
  const [listening, setListening] = useState(false);
  const [voiceOn, setVoiceOn] = useState(true);
  const [messages, setMessages] = useState(() => workspace.aynara_log.slice(0, 6).reverse().flatMap((l) => [{ role: 'user', text: l.text }, { role: 'aynara', text: l.reply }]));
  const recRef = useRef(null);
  const audioRef = useRef(null);
  const endRef = useRef(null);
  const supported = typeof window !== 'undefined' && (window.SpeechRecognition || window.webkitSpeechRecognition);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const speak = async (reply) => {
    if (!voiceOn) return;
    try {
      const res = await fetch(`${API}/chat/tts`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ text: reply.slice(0, 600) }) });
      if (!res.ok) return;
      const url = URL.createObjectURL(await res.blob());
      if (audioRef.current) audioRef.current.pause();
      audioRef.current = new Audio(url);
      audioRef.current.play().catch(() => {});
    } catch { /* silencioso */ }
  };

  const send = async (command) => {
    const value = (command || text).trim();
    if (!value) return;
    setText('');
    setMessages((m) => [...m, { role: 'user', text: value }]);
    try {
      const data = await api('/aynara/command', { method: 'POST', body: JSON.stringify({ text: value, source: command ? 'voz' : 'texto' }) }, { key: 'aynara' });
      setMessages((m) => [...m, { role: 'aynara', text: data.reply, action: data.action }]);
      speak(data.reply);
    } catch {
      setMessages((m) => [...m, { role: 'aynara', text: 'No he podido procesar la orden. Inténtalo de nuevo.' }]);
    }
  };

  const toggleMic = () => {
    if (listening) { recRef.current?.stop(); setListening(false); return; }
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { toast.error('Tu navegador no soporta dictado por voz. Escribe la orden.'); return; }
    const rec = new SR();
    rec.lang = 'es-ES'; rec.interimResults = false; rec.maxAlternatives = 1;
    rec.onresult = (e) => { const t = e.results[0][0].transcript; setListening(false); send(t); };
    rec.onerror = (e) => { setListening(false); toast.error(e?.error === 'not-allowed' ? 'Permite el acceso al micrófono para hablar con Aynara.' : 'No pude escucharte. Prueba de nuevo o escribe la orden.'); };
    rec.onend = () => setListening(false);
    recRef.current = rec;
    try { rec.start(); setListening(true); toast('Escuchando… habla ahora'); } catch { toast.error('No se pudo iniciar el micrófono. Escribe la orden.'); }
  };

  return (
    <div className="grid gap-5 lg:grid-cols-3">
      <Card className="lg:col-span-2 flex flex-col" testId="mlm-aynara-console">
        <SectionTitle kicker="Aynara · asistente por voz" title="Dile qué hacer y lo hace" subtitle="Crea prospectos, genera posts, redacta emails, programa seguimientos, registra ventas y socios. Habla o escribe."
          action={<button type="button" onClick={() => setVoiceOn((v) => !v)} className={btnGhost} data-testid="mlm-aynara-voice-toggle"><Volume2 className="h-4 w-4" />{voiceOn ? 'Voz activada' : 'Voz silenciada'}</button>} />
        <div className="mt-5 flex-1 space-y-3 overflow-y-auto rounded-2xl bg-[#f1f5fb] p-4" style={{ maxHeight: 420, minHeight: 260 }} data-testid="mlm-aynara-messages">
          {messages.length === 0 && <p className="text-sm text-slate-500">Pulsa el micrófono o escribe una orden. Ejemplo: “Aynara, dame el resumen del día”.</p>}
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm ${m.role === 'user' ? 'bg-[#0B3F8C] text-white' : 'bg-white text-slate-800 shadow-sm'}`}>
                <p>{m.text}</p>
                {m.action?.wa_link && <a href={m.action.wa_link} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1 rounded-full bg-emerald-500 px-3 py-1 text-xs font-bold text-white" data-testid="mlm-aynara-wa-link"><MessageCircle className="h-3 w-3" />Abrir WhatsApp</a>}
                {m.action?.text && !m.action?.wa_link && <p className="mt-2 rounded-lg bg-[#f1f5fb] px-3 py-2 text-xs text-slate-600">{m.action.text}</p>}
                {m.action?.section && m.action.section !== 'overview' && <button type="button" onClick={() => setSection(m.action.section)} className="mt-2 block text-xs font-bold text-[#E2001A] underline" data-testid="mlm-aynara-go-section">Ver en el panel →</button>}
              </div>
            </div>
          ))}
          {busy === 'aynara' && <p className="text-xs text-slate-400">Aynara está trabajando…</p>}
          <div ref={endRef} />
        </div>
        <form onSubmit={(e) => { e.preventDefault(); send(); }} className="mt-4 flex gap-2">
          <button type="button" onClick={toggleMic} disabled={!supported} title={supported ? 'Hablar' : 'Tu navegador no soporta voz; escribe la orden'} className={`${listening ? btnAccent : btnPrimary} !px-4 ${listening ? 'animate-pulse' : ''}`} data-testid="mlm-aynara-mic">{listening ? <MicOff className="h-5 w-5" /> : <Mic className="h-5 w-5" />}</button>
          <input value={text} onChange={(e) => setText(e.target.value)} placeholder={listening ? 'Escuchando…' : 'Escribe una orden para Aynara'} className={inputCls} data-testid="mlm-aynara-input" />
          <button type="submit" disabled={!text.trim() || busy === 'aynara'} className={`${btnPrimary} !px-4`} data-testid="mlm-aynara-send"><Send className="h-4 w-4" /></button>
        </form>
      </Card>
      <Card testId="mlm-aynara-examples">
        <SectionTitle kicker="Comandos" title="Prueba estas órdenes" />
        <div className="mt-4 space-y-2">
          {EXAMPLES.map((ex) => <button key={ex} type="button" onClick={() => send(ex)} className="block w-full rounded-xl bg-[#f1f5fb] px-3 py-2 text-left text-sm text-slate-700 transition hover:bg-[#0B3F8C] hover:text-white" data-testid="mlm-aynara-example">“{ex}”</button>)}
        </div>
        <p className="mt-4 text-xs text-slate-400">Aynara también responde por WhatsApp en {workspace.profile.aynara_whatsapp} cuando se active el canal oficial.</p>
      </Card>
    </div>
  );
};
